import EditIcon from '@edna/icons/edit.svg';
import ExitIcon from '@edna/icons/exit.svg';
import HelpIcon from '@edna/icons/help.svg';

import {
  AnalyticsIcon,
  AudienceIcon,
  BroadcastIcon,
  CategoriesIcon,
  CoinsIcon,
  FlagIcon,
  HomeIcon,
  IntegrationIcon,
  ManagerIcon,
  MoneyIcon,
  OrganisationIcon,
  ProfileIcon,
  ReferralIcon,
  SendIcon,
  SmsIcon,
  TemplateIcon,
  TriggersIcon,
} from 'src/icons';
import PATHS from 'src/paths';
import {EMetricsId} from 'src/utils';

export enum EMenuId {
  DIALOGS = 'DIALOGS',
  HOME = 'HOME',
  BROADCASTS = 'BROADCASTS',
  TRIGGERS = 'TRIGGERS',
  ANALYTICS = 'ANALYTICS',
  ANALYTICS_MESSAGES = 'ANALYTICS_MESSAGES',
  ANALYTICS_SUMMARY_REPORT = 'ANALYTICS_SUMMARY_REPORT',
  ANALYTICS_MESSAGE_MATCHERS = 'ANALYTICS_MESSAGE_MATCHERS',
  MESSAGE_MATCHERS = 'MESSAGE_MATCHERS',
  CHANNELS = 'CHANNELS',
  SUBSCRIBERS = 'SUBSCRIBERS',
  CASCADES = 'CASCADES',
  CORRECTIONS = 'CORRECTIONS',
  TARIFFS = 'TARIFFS',
  INTEGRATION = 'INTEGRATION',
  CLIENTS = 'CLIENTS',
  SUPPORT_CLIENTS = 'SUPPORT_CLIENTS',
  COMPANY_LEAD_CLIENTS = 'COMPANY_LEAD_CLIENTS',
  COMPANY_AGENT_CLIENTS = 'COMPANY_AGENT_CLIENTS',
  AGENT_PAYMENTS = 'AGENT_PAYMENTS',
  AGENT_CLIENTS = 'AGENT_CLIENTS',
  AGENT_PROFILE = 'AGENT_PROFILE',
  PROFILE = 'PROFILE',
  MY_PROFILE = 'MY_PROFILE',
  COMPANY_PROFILE = 'COMPANY_PROFILE',
  COMPANY_NEWS = 'COMPANY_NEWS',
  HELP = 'HELP',
  NOTIFICATIONS = 'NOTIFICATIONS',
  SIGN_OUT = 'SIGN_OUT',
}

export type TNavigation = {
  id: EMenuId;
  href?: string;
  icon?: TSvgComponentType;
  list?: TNavigation[];
  params?: TAnyObject;
};

export const topMenu: TNavigation[] = [
  {
    id: EMenuId.DIALOGS,
    href: PATHS.DIALOGS,
    icon: SmsIcon,
  },
  {
    id: EMenuId.HOME,
    href: PATHS.HOME,
    icon: HomeIcon,
  },
  {
    id: EMenuId.BROADCASTS,
    href: PATHS.BROADCASTS_LIST,
    icon: BroadcastIcon,
  },
  {
    id: EMenuId.TRIGGERS,
    href: PATHS.TRIGGERS_LIST,
    icon: TriggersIcon,
  },
  {
    id: EMenuId.ANALYTICS,
    icon: AnalyticsIcon,
    list: [
      {id: EMenuId.ANALYTICS_MESSAGES, href: PATHS.ANALYTICS_MESSAGES},
      {id: EMenuId.ANALYTICS_SUMMARY_REPORT, href: PATHS.ANALYTICS_SUMMARY_REPORT},
      {id: EMenuId.ANALYTICS_MESSAGE_MATCHERS, href: PATHS.ANALYTICS_MESSAGE_MATCHERS_LIST},
    ],
  },
  {
    id: EMenuId.MESSAGE_MATCHERS,
    href: PATHS.MESSAGE_MATCHERS,
    icon: TemplateIcon,
  },
  {
    id: EMenuId.CHANNELS,
    href: PATHS.CHANNELS,
    icon: SendIcon,
  },
  {
    id: EMenuId.SUBSCRIBERS,
    href: PATHS.SUBSCRIBERS_LIST,
    icon: AudienceIcon,
  },
  {
    id: EMenuId.CASCADES,
    href: PATHS.CASCADES_LIST,
    icon: CategoriesIcon,
  },
  {
    id: EMenuId.CORRECTIONS,
    href: PATHS.CORRECTIONS,
    icon: EditIcon,
  },
  {
    id: EMenuId.TARIFFS,
    href: PATHS.TARIFFS,
    icon: MoneyIcon,
  },
  {
    id: EMenuId.INTEGRATION,
    href: PATHS.INTEGRATION,
    icon: IntegrationIcon,
  },
  {
    id: EMenuId.CLIENTS,
    icon: ManagerIcon,
    list: [
      {id: EMenuId.SUPPORT_CLIENTS, href: PATHS.SUPPORT},
      {id: EMenuId.COMPANY_LEAD_CLIENTS, href: PATHS.COMPANY_CLIENTS},
      {id: EMenuId.COMPANY_AGENT_CLIENTS, href: PATHS.COMPANY_AGENT_CLIENTS},
    ],
  },

  // Agent menu
  {
    id: EMenuId.AGENT_PAYMENTS,
    href: PATHS.AGENT_PAYMENTS,
    icon: CoinsIcon,
  },
  {
    id: EMenuId.AGENT_CLIENTS,
    href: PATHS.AGENT_CLIENTS,
    icon: ReferralIcon,
  },
  {
    id: EMenuId.AGENT_PROFILE,
    href: PATHS.AGENT_PROFILE,
    icon: OrganisationIcon,
  },
];

export const bottomMenu: TNavigation[] = [
  {
    id: EMenuId.PROFILE,
    icon: ProfileIcon,
    list: [
      {
        id: EMenuId.MY_PROFILE,
        href: PATHS.PROFILE,
        icon: ProfileIcon,
      },
      {
        id: EMenuId.COMPANY_PROFILE,
        href: PATHS.COMPANY_PROFILE,
      },
      {
        id: EMenuId.COMPANY_NEWS,
        href: PATHS.NEWS,
      },
    ],
  },
  {
    id: EMenuId.HELP,
    href: PATHS.HELP,
    icon: HelpIcon,
  },
  {
    id: EMenuId.NOTIFICATIONS,
    href: PATHS.NOTIFICATIONS,
    icon: FlagIcon,
  },
  {
    id: EMenuId.SIGN_OUT,
    href: PATHS.SIGN_OUT,
    icon: ExitIcon,
  },
];

export const menuItemMetricsHash: Partial<Record<EMenuId, EMetricsId>> = {
  [EMenuId.HOME]: EMetricsId.MENU_MAIN_CLICK,
  [EMenuId.BROADCASTS]: EMetricsId.MENU_BROADCASTS_CLICK,
  [EMenuId.MESSAGE_MATCHERS]: EMetricsId.MENU_TEMPLATES_CLICK,
  [EMenuId.TRIGGERS]: EMetricsId.MENU_TRIGGERS_CLICK,
  [EMenuId.CHANNELS]: EMetricsId.MENU_CHANNELS_CLICK,
  [EMenuId.SUBSCRIBERS]: EMetricsId.MENU_AUDIENCE_CLICK,
  [EMenuId.ANALYTICS_MESSAGES]: EMetricsId.MENU_MESSAGE_REPORT_CLICK,
  [EMenuId.ANALYTICS_SUMMARY_REPORT]: EMetricsId.MENU_SUMMARY_REPORT_CLICK,
  [EMenuId.ANALYTICS_MESSAGE_MATCHERS]: EMetricsId.MENU_TEMPLATE_REPORT_CLICK,
  [EMenuId.CASCADES]: EMetricsId.MENU_CASCADES_CLICK,
  [EMenuId.INTEGRATION]: EMetricsId.MENU_INTEGRATION_CLICK,
  [EMenuId.MY_PROFILE]: EMetricsId.MENU_MY_PROFILE_CLICK,
  [EMenuId.COMPANY_PROFILE]: EMetricsId.MENU_COMPANY_PROFILE_CLICK,
  [EMenuId.COMPANY_NEWS]: EMetricsId.MENU_NEWS_CLICK,
  [EMenuId.HELP]: EMetricsId.MENU_HELP_CLICK,
};
